import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * R-03 — a full-size look at a piece, with zoom.
 *
 * A wholesale buyer is deciding whether a setting is clean, how a bezel sits
 * and what the stone actually looks like, from photographs on a screen. The
 * catalog frame is ~420px; the files behind it are far larger. Showing them at
 * a quarter of their resolution was throwing away the only detail the buyer has
 * to judge by.
 *
 * **Zoom is a transform, not a second request.** The same `src` is scaled with
 * `transform: scale()` and the origin moved to the pointer, so opening the
 * lightbox and magnifying it costs nothing beyond the bytes already fetched —
 * which matters on this catalog, where a design carries nine photographs and a
 * retailer flicks through all of them.
 *
 * Not a library. Three behaviours — click to magnify, move to pan, arrows to
 * change photograph — and every one of them is a few lines; a dependency here
 * would be more code than this, not less, and would bring its own focus and
 * scroll-locking opinions to argue with.
 */
export function Lightbox(
  { images, index, onIndex, onClose }: {
    images: readonly string[];
    index: number;
    onIndex: (next: number) => void;
    onClose: () => void;
  },
) {
  const { t } = useTranslation();
  const [zoomed, setZoomed] = useState(false);
  /** Where the pointer is, as a percentage — the transform origin while zoomed. */
  const [origin, setOrigin] = useState({ x: 50, y: 50 });
  const closeRef = useRef<HTMLButtonElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  const go = useCallback((delta: number) => {
    if (images.length < 2) return;
    // Wraps, because a buyer flicking through nine photographs should not hit a
    // wall at either end and have to reverse.
    onIndex((index + delta + images.length) % images.length);
    setZoomed(false);
  }, [images.length, index, onIndex]);

  /*
   * Escape closes, arrows move. Bound to the document rather than the dialog so
   * it works before anything inside has been clicked — a keyboard user pressing
   * Escape immediately should not have to find focus first.
   */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); onClose(); return; }
      if (e.key === 'ArrowRight') { e.preventDefault(); go(1); return; }
      if (e.key === 'ArrowLeft') { e.preventDefault(); go(-1); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [go, onClose]);

  /*
   * The page behind must not scroll while this is open, or a trackpad flick
   * moves the catalog under the overlay and the buyer closes it somewhere else
   * entirely. Restores the previous value rather than assuming it was ''.
   */
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();
    return () => { document.body.style.overflow = previous; };
  }, []);

  /** Keeps Tab inside the dialog: a modal a keyboard can walk out of is not one. */
  const onKeyDownTrap = (e: React.KeyboardEvent) => {
    if (e.key !== 'Tab') return;
    const focusable = dialogRef.current?.querySelectorAll<HTMLElement>('button');
    if (!focusable || focusable.length === 0) return;
    const first = focusable[0]!;
    const last = focusable[focusable.length - 1]!;
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  };

  const track = (e: React.MouseEvent<HTMLElement>) => {
    if (!zoomed) return;
    const r = e.currentTarget.getBoundingClientRect();
    setOrigin({
      x: ((e.clientX - r.left) / r.width) * 100,
      y: ((e.clientY - r.top) / r.height) * 100,
    });
  };

  const toggleZoom = (e: React.MouseEvent<HTMLElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    // Magnify around the point that was clicked, not the centre: a buyer clicks
    // the stone because the stone is what they want bigger.
    setOrigin({
      x: ((e.clientX - r.left) / r.width) * 100,
      y: ((e.clientY - r.top) / r.height) * 100,
    });
    setZoomed((z) => !z);
  };

  const src = images[index] ?? images[0];
  if (!src) return null;

  return (
    <div
      className="mk-lightbox"
      role="dialog"
      aria-modal="true"
      aria-label={t('shop.variant.zoomTitle')}
      ref={dialogRef}
      onKeyDown={onKeyDownTrap}
      /* Clicking the backdrop closes; clicking the photograph does not, which is
         why the figure below stops the event rather than the backdrop testing
         its target. */
      onClick={onClose}
    >
      <div className="mk-lightbox-bar" onClick={(e) => e.stopPropagation()}>
        <span className="mk-lightbox-count">
          {images.length > 1
            ? t('shop.variant.imageAlt', { n: index + 1, count: images.length })
            : null}
        </span>
        <button
          type="button"
          ref={closeRef}
          className="mk-lightbox-btn"
          onClick={onClose}
          aria-label={t('shop.variant.zoomClose')}
        >
          <span aria-hidden="true">&times;</span>
        </button>
      </div>

      {images.length > 1 ? (
        <button
          type="button"
          className="mk-lightbox-nav prev"
          onClick={(e) => { e.stopPropagation(); go(-1); }}
          aria-label={t('shop.variant.zoomPrev')}
        >
          <span aria-hidden="true">&#8249;</span>
        </button>
      ) : null}

      <figure
        className={`mk-lightbox-figure${zoomed ? ' zoomed' : ''}`}
        onClick={(e) => { e.stopPropagation(); toggleZoom(e); }}
        onMouseMove={track}
        onMouseLeave={() => setOrigin({ x: 50, y: 50 })}
      >
        <img
          src={src}
          alt=""
          style={zoomed ? { transformOrigin: `${origin.x}% ${origin.y}%` } : undefined}
          draggable={false}
        />
      </figure>

      {images.length > 1 ? (
        <button
          type="button"
          className="mk-lightbox-nav next"
          onClick={(e) => { e.stopPropagation(); go(1); }}
          aria-label={t('shop.variant.zoomNext')}
        >
          <span aria-hidden="true">&#8250;</span>
        </button>
      ) : null}

      <p className="mk-lightbox-hint" onClick={(e) => e.stopPropagation()}>
        {zoomed ? t('shop.variant.zoomOutHint') : t('shop.variant.zoomInHint')}
      </p>
    </div>
  );
}
